import React from "react";
import { useState } from "react";
import styled from "styled-components";
import { useProvided } from "nonaction";
import UploadButton from "./Upload.js";
import SettingsPanel from "./Settings.js";
import {
  ImageAssetsContainer,
  PrintSettingsContainer,
  TextContainer
} from "../../Container";
import {
  createImageAssetMap,
  extractLocalImagePaths
} from "../../Lib/markdownImages.js";
import {
  clearPersistedImageFiles,
  persistImageFiles
} from "../../Lib/imageAssetStore.js";
import {
  initialText,
  initialTextUrl,
  resumeTemplates
} from "../../Container/Hooks/InitialText.js";

const CSS_PIXELS_PER_MM = 96 / 25.4;
const PAPER_SIZES = {
  A4: { width: 210, height: 297 },
  Letter: { width: 215.9, height: 279.4 }
};

const pageDimensions = settings => {
  const paper = settings.pageSize === "custom"
    ? { width: settings.customWidth, height: settings.customHeight }
    : PAPER_SIZES[settings.pageSize] || PAPER_SIZES.A4;
  return settings.orientation === "landscape"
    ? { width: paper.height, height: paper.width }
    : paper;
};

const escapeHtml = value => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

const printLayoutCss = settings => `
  .markdown-body,
  .preview {
    zoom: ${settings.scale / 100};
  }
  .markdown-body {
    padding-top: ${settings.header ? 10 : 0}mm;
    padding-bottom: ${settings.footer || settings.pageNumbers ? 10 : 0}mm;
  }
  .preview {
    padding-top: ${settings.header ? 10 : 0}mm !important;
    padding-bottom: ${settings.footer || settings.pageNumbers ? 10 : 0}mm !important;
  }
  h1, h2, h3, h4, h5, h6,
  table, tr, img, figure, blockquote, pre {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  h1, h2, h3, h4, h5, h6 {
    break-after: avoid;
    page-break-after: avoid;
  }
  .markdown-body .page-break {
    break-before: ${settings.singlePage ? "avoid" : "page"} !important;
    page-break-before: ${settings.singlePage ? "auto" : "always"} !important;
    height: 0;
  }
`;

const printDecorationMarkup = settings => {
  const hasHeader = Boolean(settings.header);
  const hasFooter = Boolean(settings.footer || settings.pageNumbers);
  return `
    ${hasHeader ? `<div class="md2pdf-print-header">${escapeHtml(settings.header)}</div>` : ""}
    ${hasFooter ? `<div class="md2pdf-print-footer">${escapeHtml(settings.footer)}${settings.pageNumbers ? ` <span class="md2pdf-page-number">Page <span class="page-number"></span></span>` : ""}</div>` : ""}
  `;
};

const printDecorationCss = settings => `
  .md2pdf-print-header,
  .md2pdf-print-footer {
    display: none;
  }
  @media print {
    .md2pdf-print-header,
    .md2pdf-print-footer {
      display: block;
      position: fixed;
      left: ${settings.margin}mm;
      right: ${settings.margin}mm;
      color: #586069;
      font-size: 9pt;
      z-index: 10;
    }
    .md2pdf-print-header {
      top: ${Math.max(settings.margin / 2, 2)}mm;
      text-align: center;
    }
    .md2pdf-print-footer {
      bottom: ${Math.max(settings.margin / 2, 2)}mm;
      text-align: center;
    }
    .page-number::after {
      content: counter(page);
    }
  }
`;

function waitForImages(document) {
  return Promise.all(
    Array.from(document.images).map(image => {
      if (image.complete) return Promise.resolve();
      return new Promise(resolve => {
        const done = () => resolve();
        image.addEventListener("load", done, { once: true });
        image.addEventListener("error", done, { once: true });
      });
    })
  );
}

function printSinglePage(previewEl, title, settings) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    window.print();
    return;
  }

  const pageStyles = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]')
  )
    .map(node => node.outerHTML)
    .join("\n");

  printWindow.document.open();
  printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <title>${title}</title>
    ${pageStyles}
    <style>
      html,
      body {
        margin: 0;
        padding: 0;
        background: white;
      }
      .markdown-body {
        box-sizing: border-box;
        width: ${pageDimensions(settings).width}mm;
        max-width: none;
        overflow: visible;
        padding: ${settings.margin}mm;
      }
      * {
        -webkit-print-color-adjust: ${settings.printBackgrounds ? "exact" : "economy"};
        print-color-adjust: ${settings.printBackgrounds ? "exact" : "economy"};
      }
      ${printLayoutCss(settings)}
      ${printDecorationCss(settings)}
      @media print {
        html,
        body,
        .markdown-body {
          height: auto !important;
          min-height: 0 !important;
          overflow: visible !important;
        }
      }
    </style>
  </head>
  <body>
    ${printDecorationMarkup(settings)}
    <main class="markdown-body">${previewEl.innerHTML}</main>
  </body>
</html>`);
  printWindow.document.close();

  const printWhenReady = async () => {
    if (printWindow.document.fonts && printWindow.document.fonts.ready) {
      await printWindow.document.fonts.ready;
    }
    await waitForImages(printWindow.document);

    await new Promise(resolve => printWindow.requestAnimationFrame(resolve));
    await new Promise(resolve => printWindow.requestAnimationFrame(resolve));

    const contentEl = printWindow.document.querySelector(".markdown-body");
    const contentHeightPx = Math.max(
      contentEl.scrollHeight,
      contentEl.offsetHeight,
      contentEl.getBoundingClientRect().height
    );
    const contentHeightMm = Math.ceil(contentHeightPx / CSS_PIXELS_PER_MM) + 12;
    const printStyle = printWindow.document.createElement("style");
    printStyle.textContent = `@page { size: ${pageDimensions(settings).width}mm ${contentHeightMm}mm; margin: 0; }`;
    printWindow.document.head.appendChild(printStyle);

    await new Promise(resolve => printWindow.requestAnimationFrame(resolve));
    await new Promise(resolve => printWindow.requestAnimationFrame(resolve));

    printWindow.focus();
    printWindow.print();
    printWindow.addEventListener("afterprint", () => printWindow.close(), {
      once: true,
    });
  };

  printWhenReady();
}

const Header = ({ className }) => {
  const [, setText] = useProvided(TextContainer);
  const [, setImageAssets] = useProvided(ImageAssetsContainer);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings] = useProvided(PrintSettingsContainer);

  const handleMarkdownLoaded = (content, markdownFile, imageFiles) => {
    if (content !== null) setText(content);
    const nextAssets = createImageAssetMap(imageFiles, markdownFile);
    setImageAssets(previousAssets =>
      markdownFile ? nextAssets : { ...previousAssets, ...nextAssets }
    );
    persistImageFiles(imageFiles, markdownFile, { replace: Boolean(markdownFile) });
  };

  const onReset = async () => {
    setImageAssets({});
    await clearPersistedImageFiles();
    try {
      const response = await fetch(initialTextUrl);
      if (!response.ok) throw new Error(`Failed to load ${initialTextUrl}`);
      setText(await response.text());
    } catch (error) {
      setText(initialText);
    }
  };

  const onLoadResume = async resumeUrl => {
    setImageAssets({});
    await clearPersistedImageFiles();
    try {
      const response = await fetch(resumeUrl);
      if (!response.ok) throw new Error(`Failed to load ${resumeUrl}`);
      const content = await response.text();
      // Register the template's relative images so the preview can resolve them
      // against the template directory (e.g. avatar.svg -> /example/avatar.svg).
      const templateDirectory = new URL(resumeUrl, window.location.href);
      const templateAssets = {};
      for (const path of extractLocalImagePaths(content)) {
        templateAssets[path] = new URL(path, templateDirectory).href;
      }
      setImageAssets(templateAssets);
      setText(content);
    } catch (error) {
      window.alert(`Failed to load the resume template (${resumeUrl}).`);
    }
  };

  const onTransfrom = async () => {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    await waitForImages(document);

    const previewEl = document.querySelector(".preview");
    let candidateTitle = "";
    const candidateTitleEl = previewEl && previewEl.querySelector("h1");
    if (candidateTitleEl) {
      candidateTitle = candidateTitleEl.innerText;
    }

    if (settings.singlePage && previewEl) {
      printSinglePage(previewEl, candidateTitle || document.title, settings);
      return;
    }

    // get the file name
    if (candidateTitle) {
      // do the effect change the title
      const currentTitle = document.title;
      document.title = candidateTitle;
      window.requestAnimationFrame(() => {
        // schedule resume back in next frame
        document.title = currentTitle;
      });
    }
    const dimensions = pageDimensions(settings);
    const printStyle = document.createElement("style");
    printStyle.dataset.md2pdfPrintSettings = "true";
    printStyle.textContent = `
      @page {
        size: ${dimensions.width}mm ${dimensions.height}mm;
        margin: ${settings.margin}mm;
      }
      * {
        -webkit-print-color-adjust: ${settings.printBackgrounds ? "exact" : "economy"};
        print-color-adjust: ${settings.printBackgrounds ? "exact" : "economy"};
      }
      ${printLayoutCss(settings)}
      ${printDecorationCss(settings)}
    `;
    document.head.appendChild(printStyle);
    const decorations = [];
    if (settings.header) {
      const header = document.createElement("div");
      header.className = "md2pdf-print-header";
      header.textContent = settings.header;
      document.body.appendChild(header);
      decorations.push(header);
    }
    if (settings.footer || settings.pageNumbers) {
      const footer = document.createElement("div");
      footer.className = "md2pdf-print-footer";
      footer.textContent = settings.footer;
      if (settings.pageNumbers) {
        const pageNumber = document.createElement("span");
        pageNumber.className = "md2pdf-page-number";
        pageNumber.textContent = " Page ";
        const counter = document.createElement("span");
        counter.className = "page-number";
        pageNumber.appendChild(counter);
        footer.appendChild(pageNumber);
      }
      document.body.appendChild(footer);
      decorations.push(footer);
    }
    window.addEventListener(
      "afterprint",
      () => {
        printStyle.remove();
        decorations.forEach(decoration => decoration.remove());
      },
      { once: true }
    );
    window.print();
  };
  return (
    <header className={className + " no-print"}>
      <p className="project"> md2pdf </p>
      <iframe
        title="github-button"
        className="project"
        style={{ display: "block" }}
        src="https://ghbtns.com/github-btn.html?user=jimyag&repo=md2pdf&type=star&count=true"
        frameBorder="0"
        scrolling="0"
        width="100px"
        height="20px"
      />

      <div className="menu">
        <UploadButton
          className="button upload"
          onMarkdownLoaded={handleMarkdownLoaded}
        />
        <p className="button reset" onClick={onReset}>
          Reset
        </p>
        <p className="button resume">
          <select
            aria-label="Resume template"
            value=""
            onChange={event => {
              const url = event.target.value;
              if (url) onLoadResume(url);
              event.target.value = "";
            }}
          >
            <option value="" disabled>
              Resume ▾
            </option>
            {resumeTemplates.map(template => (
              <option key={template.url} value={template.url}>
                {template.label}
              </option>
            ))}
          </select>
        </p>
        <p className="button settings" onClick={() => setSettingsOpen(true)}>
          Settings
        </p>
        <p className="button download" onClick={onTransfrom}>
          <span role="img" aria-label="download">
            🎉
          </span>
          <span>Transform</span>
        </p>
      </div>
      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}
      {/* <span className="author">Powered by @realdennis</span> */}
    </header>
  );
};

export default styled(Header)`
  * {
    box-sizing: border-box;
  }
  flex-shrink: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  user-select: none;
  padding-left: 5px;
  padding-right: 5px;
  color: black;
  background-color: rgb(233, 233, 233);
  display: flex;
  align-items: center;
  height: 40px;
  .project {
    font-weight: bold;
    margin: 5px;
    flex-shrink: 0;
    height: 20px;
  }
  div.menu {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    .button {
      height: 80%;
      margin: 0;
      display: flex;
      align-items: center;
      margin-left: 3px;
      border-radius: 3px;
      border: 1px solid black;
      padding: 10px;
      cursor: pointer;
    }
    .button.resume select {
      border: none;
      background: transparent;
      color: inherit;
      font: inherit;
      cursor: pointer;
      outline: none;
    }
  }

  /* span.author {
    position: fixed;
    bottom: 2px;
    left: 2px;
    opacity: 0.5;
    color: white;
    height: 20px;
    z-index:99;
  } */
  @keyframes dance {
    0% {
      transform: rotate(3deg);
    }
    100% {
      transform: rotate(-2deg);
    }
  }
`;
